import { inject, Injectable } from '@angular/core';

import type { ConversationStarter } from '@core/tellent-assistant-types';

import { AssistantRunService, type OutgoingMessage } from '../run/assistant-run.service';
import { TellentAssistantActionsService } from '../tellent-assistant-actions.service';
import { TellentAssistantThreadsService } from '../threads/tellent-assistant-threads.service';

@Injectable({ providedIn: 'root' })
export class TellentAssistantService {
  private readonly run = inject(AssistantRunService);
  private readonly threadsService = inject(TellentAssistantThreadsService);
  private readonly actionsService = inject(TellentAssistantActionsService);

  public readonly messages = this.run.messages;
  public readonly runStatus = this.run.runStatus;
  public readonly currentThreadId = this.run.currentThreadId;
  public readonly lastError = this.run.lastError;

  public send(text: string, metadata?: Record<string, unknown>): void {
    if (this.run.runStatus() === 'running') return;
    this.run.sendMessages(this.withOutcomeReport([{ role: 'user', content: text, metadata }]));
  }

  public sendStarter(starter: ConversationStarter): void {
    if (this.run.runStatus() === 'running') return;
    this.run.sendMessages(this.withOutcomeReport(starter.messages));
  }

  public cancelRun(): void {
    this.run.cancelRun();
  }

  public newThread(): void {
    this.run.resetThread();
  }

  public resetThread(): void {
    this.run.resetThread();
  }

  public switchThread(id: string): void {
    this.run.cancelRun();
    this.run.switchToExistingThread(id);
    this.threadsService.loadCurrent(id);
  }

  /**
   * Proposal decisions happen outside the conversation (popover → backend),
   * so the agent is blind to them. Prepend any undelivered outcomes as a
   * hidden UI-event user message — user-role so the decision survives in
   * thread memory (a system message is dropped after the turn, leaving the
   * agent amnesiac about its own executed proposals) — before the message
   * that triggered this run.
   */
  private withOutcomeReport(messages: readonly OutgoingMessage[]): readonly OutgoingMessage[] {
    const threadId = this.run.currentThreadId();
    const report = threadId ? this.actionsService.drainOutcomeReport(threadId) : null;
    if (!report) return messages;
    return [
      {
        role: 'user',
        content: `[UI decisions — not typed] ${report}`,
        metadata: { uiEvent: 'action-outcomes' },
      },
      ...messages,
    ];
  }
}
